"use client";

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, RefreshCw, Loader2 } from "lucide-react";
import { useAuthStore } from "@/lib/store";
import { useTranslation } from "@/lib/hooks/useTranslation";
import { toast } from "sonner";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

export function EmailVerifyModal() {
  const [open, setOpen] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { user, accessToken, setUser } = useAuthStore();
  const { locale } = useTranslation();

  // Listen for the global trigger event dispatched by handleApiError
  useEffect(() => {
    const handler = () => {
      if (user && !user.email_verified) {
        setOpen(true);
      }
    };
    window.addEventListener("velyq:email-verify-required", handler);
    return () => window.removeEventListener("velyq:email-verify-required", handler);
  }, [user]);

  // Auto-close when the user becomes verified while the modal is open
  useEffect(() => {
    if (user?.email_verified && open) {
      setOpen(false);
    }
  }, [user?.email_verified, open]);

  // Mirror the banner's handleResend: POST with email query param (no auth header)
  const handleResend = async () => {
    if (!user || isResending) return;
    setIsResending(true);
    try {
      const res = await fetch(
        `${API_URL}/api/v1/auth/resend-verification?email=${encodeURIComponent(user.email)}`,
        { method: "POST" },
      );
      const data = await res.json();
      if (res.ok) {
        if (data.message?.includes("already verified")) {
          toast.info(
            locale === "fr"
              ? "Email déjà vérifié ! Actualisation..."
              : "Email already verified! Refreshing…",
          );
          handleRefresh();
        } else {
          setResent(true);
          toast.success(
            locale === "fr"
              ? "Email de vérification envoyé !"
              : "Verification email sent!",
          );
        }
      } else {
        toast.error(
          locale === "fr"
            ? "Erreur lors de l'envoi"
            : "Failed to send the email",
        );
      }
    } catch {
      toast.error(
        locale === "fr" ? "Erreur de connexion" : "Connection error",
      );
    } finally {
      setIsResending(false);
    }
  };

  // Mirror the banner's handleRefresh: GET /users/me with auth header
  const handleRefresh = async () => {
    if (!accessToken || isRefreshing) return;
    setIsRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/users/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
        if (userData.email_verified) {
          toast.success(
            locale === "fr" ? "Email vérifié !" : "Email verified!",
          );
          setOpen(false);
        } else {
          toast.info(
            locale === "fr"
              ? "Pas encore vérifié. Pense à cliquer sur le lien reçu."
              : "Not verified yet. Don't forget to click the link in the email.",
          );
        }
      } else {
        toast.error(locale === "fr" ? "Erreur de connexion" : "Connection error");
      }
    } catch {
      toast.error(locale === "fr" ? "Erreur de connexion" : "Connection error");
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!user) return null;

  const fr = locale === "fr";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Mail className="h-5 w-5 text-primary" />
            </div>
          </div>
          <DialogTitle>
            {fr ? "Vérifie ton email pour continuer" : "Verify your email to continue"}
          </DialogTitle>
          <DialogDescription>
            {fr
              ? `On a envoyé un lien à ${user.email}. Clique dessus pour activer cette fonctionnalité.`
              : `We sent a link to ${user.email}. Click it to unlock this feature.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleResend}
            disabled={isResending || resent}
          >
            {isResending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Mail className="mr-2 h-4 w-4" />
            )}
            {resent
              ? fr ? "Renvoyé ✓" : "Sent ✓"
              : fr ? "Renvoyer le lien" : "Resend link"}
          </Button>
          <Button onClick={handleRefresh} disabled={isRefreshing}>
            {isRefreshing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            {fr ? "J'ai vérifié" : "I verified"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
